import { useCallback, useEffect } from 'react';

import WebApp from '@twa-dev/sdk';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTE_PATHS } from '@/shared/config/router/types';
import { getOrCreateElement, removeElement } from '@/shared/helpers/dom';
import { isTMA } from '@/shared/helpers/is-tma';

import { useFeatureFlag } from '../use-feature-flag';
import {
  DEV_BUTTON_ID,
  DEV_SETTINGS_BUTTON_ID,
  TOP_SAFE_AREA_WRAPPER_ID,
} from './constants';

/**
 * Custom hook to handle Telegram's WebApp settings button.
 */
export const useTelegramSettingsButton = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { isKassir } = useFeatureFlag();

  const IS_DEV_ENV = import.meta.env.DEV && !isTMA();
  const isVisible = pathname !== ROUTE_PATHS.SETTINGS;

  /**
   * Configures the fake settings button in development mode.
   */
  const setupSettingsButton = useCallback(
    (isVisible: boolean) => {
      if (!IS_DEV_ENV) return;

      const container = getOrCreateElement(TOP_SAFE_AREA_WRAPPER_ID);
      container.className = 'top-safe-area-wrapper';

      const button = getOrCreateElement(DEV_SETTINGS_BUTTON_ID, container, 'button');

      button.className = 'dev-settings-button';
      button.style.visibility = isVisible ? 'visible' : 'hidden';

      button.onclick = () => navigate(ROUTE_PATHS.SETTINGS);
    },
    [IS_DEV_ENV, navigate]
  );

  /**
   * Configures the dev button in development mode.
   */
  const setupDevButton = useCallback(() => {
    if (!IS_DEV_ENV) return;

    const container = getOrCreateElement(TOP_SAFE_AREA_WRAPPER_ID);
    container.className = 'top-safe-area-wrapper';

    const button = getOrCreateElement(DEV_BUTTON_ID, container, 'button');

    button.className = 'dev-button';

    button.onclick = () => navigate(ROUTE_PATHS.DEV);
  }, [IS_DEV_ENV, navigate]);

  useEffect(() => {
    const handleClick = () => navigate(ROUTE_PATHS.SETTINGS);

    if (isTMA()) WebApp.SettingsButton.onClick(handleClick);

    document
      .getElementById(DEV_SETTINGS_BUTTON_ID)
      ?.addEventListener('click', handleClick);

    return () => {
      if (isTMA()) WebApp.SettingsButton.offClick(handleClick);

      document
        .getElementById(DEV_SETTINGS_BUTTON_ID)
        ?.removeEventListener('click', handleClick);
    };
  }, [navigate]);

  useEffect(() => {
    if (!isTMA()) return;

    if (isVisible) WebApp.SettingsButton.show();
    else WebApp.SettingsButton.hide();
  }, [isVisible]);

  useEffect(() => {
    return () => {
      removeElement(
        DEV_SETTINGS_BUTTON_ID,
        document.getElementById(TOP_SAFE_AREA_WRAPPER_ID) as HTMLElement
      );
      WebApp.SettingsButton.hide();
    };
  }, []);

  useEffect(() => {
    if (isKassir) setupDevButton();
    setupSettingsButton(isVisible);
  }, [IS_DEV_ENV, isKassir, isVisible, navigate, setupDevButton, setupSettingsButton]);
};
